import React, { useCallback, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { useTranslation } from "react-i18next";
import {
    CountByPrimaryFilter,
    EmployeeData,
    MonitorViewFleetSummaryTotals,
    VehicleData,
} from "../../../models/monitorView/monitorViewTypes";
import VehicleFilters from "./filters/vehicleFilters";
import VehicleGroupFilter from "./filters/vehicleGroupFilter";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import SortIcon from "@mui/icons-material/Sort";
import PersonIcon from "@mui/icons-material/Person";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import { useAppDispatch, useAppSelector } from "../../../framework/customStore";
import {
    setSelectedVehicleGroupIds,
    setSortOrder,
    SortOrder,
} from "../../../store/monitorViewFiltersSlice";
import { MonitorViewWorktimeFilters } from "./filters/monitorViewWorktimeFilters";
import { handleVehicleSelect, hasActiveWorkTime } from "../monitorViewUtils";
import {
    setSelectedEmployeeId,
    toggleWorkShiftView,
} from "../../../store/monitorViewSlice";
import { getEmployeeGroups } from "../../../services/employeeGroupService";
import { useFetch } from "../../../hooks/useFetch";
import { employeeGroupListItemDto } from "../../../models/employeeGroup/employeeGroup";
import FleetOverviewCard from "./fleetOverviewCard/fleetOverviewCard";

interface MonitorViewOverviewSidebarProps {
    vehicleData: VehicleData[];
    employeeData: EmployeeData[];
    countByPrimaryFilter: CountByPrimaryFilter;
    fleetSummaryTotals: MonitorViewFleetSummaryTotals;
}

export const MonitorViewOverviewSidebar = ({
    vehicleData,
    employeeData,
    countByPrimaryFilter,
    fleetSummaryTotals,
}: MonitorViewOverviewSidebarProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const { selectedVehicleGroupIds, sortOrder } = useAppSelector(
        (state) => state.monitorViewFilters
    );
    const { selectedVehicleId, isVehicleDetailView, selectedEmployeeId } =
        useAppSelector((state) => state.monitorView);
    const [employeeGroups, setEmployeeGroups] =
        useState<employeeGroupListItemDto[]>();
    const [searchText, setSearchText] = useState("");
    const positions = useAppSelector((state) => state.monitorView.positions);
    const employees = useAppSelector((state) => state.employees.employees);

    // Build a Map from employeeId (lowercase) -> position for O(1) lookups
    const positionByEmployeeId = useMemo(() => {
        const map = new Map<string, (typeof positions)[number]>();
        for (const p of positions ?? []) {
            for (const w of p.employeeWorkTimeTypes ?? []) {
                if (w.employeeId) {
                    map.set(w.employeeId.toLowerCase(), p);
                }
            }
        }
        return map;
    }, [positions]);

    const countByWorkTimeType = useMemo(() => {
        const counts =
            employees?.reduce(
                (acc, e) => {
                    const position = e.id
                        ? positionByEmployeeId.get(e.id.toLowerCase())
                        : undefined;
                    const workTimeTypeId =
                        position?.employeeWorkTimeTypes[0]?.workTimeTypeId;
                    if (!position || workTimeTypeId === null) {
                        acc["noWork"] = (acc["noWork"] || 0) + 1;
                    } else if (workTimeTypeId === undefined) {
                        // Employee working but no work phase assigned
                        acc["workNoPhase"] = (acc["workNoPhase"] || 0) + 1;
                    } else {
                        acc[workTimeTypeId] = (acc[workTimeTypeId] || 0) + 1;
                        acc["work"] = (acc["work"] || 0) + 1;
                    }
                    return acc;
                },
                {} as { [key: string]: number }
            ) || {};
        return counts;
    }, [positionByEmployeeId, employees]);

    const countByDrivingStatus = useMemo(() => {
        // Count telematics status only for vehicles without active work time
        const counts =
            positions?.reduce(
                (acc, p) => {
                    if (!hasActiveWorkTime(p)) {
                        const drivingStatus = p.driving;
                        acc[drivingStatus] = (acc[drivingStatus] || 0) + 1;
                    }
                    return acc;
                },
                {} as { [key: number]: number }
            ) || {};
        return counts;
    }, [positions]);

    const onSelectVehicleId = (vehicleId: string) => {
        handleVehicleSelect(
            vehicleId,
            vehicleData,
            isVehicleDetailView,
            dispatch
        );
    };

    const onSelectEmployeeId = (employeeId: string) => {
        dispatch(setSelectedEmployeeId(employeeId));
        dispatch(toggleWorkShiftView());
    };

    useFetch(
        (signal) => getEmployeeGroups(true, signal),
        {
            onSuccess: (res) =>
                setEmployeeGroups(
                    res.data.filter((d) => !!d.salaryPeriodTypeId)
                ),
        },
        []
    );

    const employeesWithoutVehicles = useMemo(() => {
        const assignedEmployeeIds = new Set(
            vehicleData?.map((v) => v.employeeId?.toLowerCase())
        );
        return employeeData?.filter(
            (employee) =>
                !assignedEmployeeIds.has(employee.employeeId?.toLowerCase())
        );
    }, [employeeData, vehicleData]);

    // Build a Map from employeeId (lowercase) -> EmployeeData for O(1) lookups
    const employeeByIdMap = useMemo(() => {
        const map = new Map<string, EmployeeData>();
        for (const e of employeeData ?? []) {
            if (e.employeeId) {
                map.set(e.employeeId.toLowerCase(), e);
            }
        }
        return map;
    }, [employeeData]);

    const findEmployeeForVehicle = useCallback(
        (vehicle: VehicleData): EmployeeData | undefined => {
            if (!vehicle.employeeId) return undefined;
            return employeeByIdMap.get(vehicle.employeeId.toLowerCase());
        },
        [employeeByIdMap]
    );

    const matchesSearch = useCallback(
        (vehicle: VehicleData | null, employee: EmployeeData | null) => {
            if (!searchText.trim()) return true;
            const term = searchText.toLowerCase().trim();
            const fields: (string | null | undefined)[] = [
                vehicle?.registerNumber,
                vehicle?.brand,
                employee?.employeeName,
                employee?.workTimeTypeName,
            ];
            // Include customer/cost center from current work shift time slots
            const slots = employee?.currentWorkShift?.workShiftTimeSlots;
            if (slots) {
                for (const slot of slots) {
                    fields.push(slot.customerName, slot.costCenterName);
                }
            }
            return fields.some((f) => f?.toLowerCase().includes(term));
        },
        [searchText]
    );

    const filteredVehicles = useMemo(
        () =>
            vehicleData?.filter((v) =>
                matchesSearch(v, findEmployeeForVehicle(v))
            ) ?? [],
        [vehicleData, matchesSearch, findEmployeeForVehicle]
    );

    const filteredEmployeesWithoutVehicles = useMemo(
        () =>
            employeesWithoutVehicles?.filter((e) => matchesSearch(null, e)) ??
            [],
        [employeesWithoutVehicles, matchesSearch]
    );

    type CardEntry =
        | { type: "vehicle"; vehicle: VehicleData; employee?: EmployeeData }
        | { type: "employeeOnly"; employee: EmployeeData };

    const sortedCards = useMemo(() => {
        const entries: CardEntry[] = [
            ...filteredVehicles.map((v) => ({
                type: "vehicle" as const,
                vehicle: v,
                employee: findEmployeeForVehicle(v),
            })),
            ...filteredEmployeesWithoutVehicles.map((e) => ({
                type: "employeeOnly" as const,
                employee: e,
            })),
        ];

        return entries.sort((a, b) => {
            const empNameA =
                a.type === "vehicle"
                    ? (a.employee?.employeeName ?? "")
                    : (a.employee.employeeName ?? "");
            const empNameB =
                b.type === "vehicle"
                    ? (b.employee?.employeeName ?? "")
                    : (b.employee.employeeName ?? "");
            const regA =
                a.type === "vehicle" ? (a.vehicle.registerNumber ?? "") : "";
            const regB =
                b.type === "vehicle" ? (b.vehicle.registerNumber ?? "") : "";

            if (sortOrder === "registerNumber") {
                // Employees without vehicles go to the bottom
                if (!regA && regB) return 1;
                if (regA && !regB) return -1;
                return regA.localeCompare(regB);
            }
            // Sort by employee name; vehicles without employee at the bottom
            const hasEmpA = !!empNameA;
            const hasEmpB = !!empNameB;
            if (!hasEmpA && hasEmpB) return 1;
            if (hasEmpA && !hasEmpB) return -1;
            return empNameA.localeCompare(empNameB);
        });
    }, [
        filteredVehicles,
        filteredEmployeesWithoutVehicles,
        findEmployeeForVehicle,
        sortOrder,
    ]);

    const renderCards = (
        <>
            {sortedCards.map((entry) =>
                entry.type === "vehicle" ? (
                    <FleetOverviewCard
                        key={`vehicle-${entry.vehicle.vehicleId}-employee-${entry.vehicle.employeeId ?? "none"}`}
                        vehicle={entry.vehicle}
                        employee={entry.employee}
                        employeeGroups={employeeGroups}
                        isSelected={
                            entry.vehicle.vehicleId === selectedVehicleId
                        }
                        onClickCard={() => {
                            onSelectVehicleId(entry.vehicle.vehicleId);
                        }}
                        onSelectEmployee={() => {
                            onSelectEmployeeId(entry.vehicle.employeeId);
                        }}
                    />
                ) : (
                    <FleetOverviewCard
                        key={`vehicle-novehicle-employee-${entry.employee.employeeId}`}
                        vehicle={null}
                        employee={entry.employee}
                        employeeGroups={employeeGroups}
                        isSelected={
                            entry.employee.employeeId === selectedEmployeeId
                        }
                        onClickCard={() => {
                            if (entry.employee.currentWorkShift) {
                                onSelectEmployeeId(entry.employee.employeeId);
                            }
                        }}
                        onSelectEmployee={() => {
                            if (entry.employee.currentWorkShift) {
                                onSelectEmployeeId(entry.employee.employeeId);
                            }
                        }}
                    />
                )
            )}
        </>
    );

    return (
        <>
            <Grid container>
                <Grid item xs={12}>
                    <VehicleFilters
                        countByPrimaryFilter={countByPrimaryFilter}
                    />
                </Grid>
                <Grid item xs={12}>
                    <MonitorViewWorktimeFilters
                        countByWorkTimeType={countByWorkTimeType}
                        countByDrivingStatus={countByDrivingStatus}
                    />
                </Grid>
                <Grid item xs={12} display="flex" justifyContent="flex-end">
                    <VehicleGroupFilter
                        selectedGroupIds={selectedVehicleGroupIds}
                        setSelectedVehicleGroupIds={setSelectedVehicleGroupIds}
                    />
                </Grid>
                <Grid item xs={12} sx={{ px: 1, py: 0.5 }}>
                    <TextField
                        size="small"
                        fullWidth
                        placeholder={t(
                            "monitorView.sidebar.overview.header.searchPlaceholder"
                        )}
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" />
                                </InputAdornment>
                            ),
                        }}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ px: 1, py: 0.5 }}
                >
                    <Typography variant="caption" color="text.secondary">
                        {t(
                            "monitorView.sidebar.overview.header.activeVehicleCountSummary",
                            { vehicles: fleetSummaryTotals.activeVehicles }
                        )}
                        {" / "}
                        {t(
                            "monitorView.sidebar.overview.header.activeEmployeeCountSummary",
                            { employees: fleetSummaryTotals.activeEmployees }
                        )}
                    </Typography>
                    <ToggleButtonGroup
                        value={sortOrder}
                        exclusive
                        onChange={(_, val: SortOrder) => {
                            if (val) dispatch(setSortOrder(val));
                        }}
                        size="small"
                    >
                        <ToggleButton
                            value="employeeName"
                            title={t("monitorView.sort.byEmployee")}
                            sx={{ px: 1, gap: 0.5 }}
                        >
                            <SortIcon sx={{ fontSize: 14 }} />
                            <PersonIcon fontSize="small" />
                        </ToggleButton>
                        <ToggleButton
                            value="registerNumber"
                            title={t("monitorView.sort.byVehicle")}
                            sx={{ px: 1, gap: 0.5 }}
                        >
                            <SortIcon sx={{ fontSize: 14 }} />
                            <DirectionsCarIcon fontSize="small" />
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Grid>
            </Grid>
            {filteredVehicles?.length > 0 ||
            filteredEmployeesWithoutVehicles?.length > 0 ? (
                renderCards
            ) : (
                <Alert variant="standard" severity="info">
                    {t("monitorView.sidebar.overview.noRows")}
                </Alert>
            )}
        </>
    );
};
